import './AltGallery.module.less';

import {
  type FileObject,
  TransactionDocumentClient,
  TransactionDocumentList,
} from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  DataTablePagination,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@kalos/ui';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageSearchTwoTone from '@mui/icons-material/ImageSearchTwoTone';
import { default as IconButton } from '@mui/material/IconButton';
import {
  DownloadIcon,
  EnterFullScreenIcon,
  ExitFullScreenIcon,
  RotateCounterClockwiseIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { ENDPOINT, WaiverTypes } from '../../constants';
import { queryKeys } from '../../hooks/react-query/constants';
import { useFilesBatchQuery } from '../../hooks/react-query/useFileQuery';
import {
  useDeleteTransactionDocumentByIDMutation,
  useTransactionDocumentDataByIdQuery,
  useTransactionDocumentsBatchQuery,
} from '../../hooks/react-query/useTransactionDocumentQuery';
import { convertHeicToJpeg, getMimeType } from '../../tools/helpers';
import { Button as ButtonLib } from '../ComponentsLibrary/Button';
import { ConfirmDelete } from '../ComponentsLibrary/ConfirmDelete';
import { Modal } from '../ComponentsLibrary/Modal';
import { type Deg } from '../ComponentsLibrary/RotatedImage';
import { SectionBar } from '../ComponentsLibrary/SectionBar';
import { Tooltip } from '../ComponentsLibrary/Tooltip';
import { Loader } from '../Loader/main';

export interface GalleryData {
  key: string;
  bucket: string;
  description: string;
  typeId: number;
}

interface Props {
  fileList: GalleryData[];
  title: string;
  text: string;
  subtitle?: string;
  iconButton?: boolean;
  disabled?: boolean;
  canDelete?: boolean;
  transactionID: number;
}

interface DocData {
  reference: string;
  id: number;
  data?: Uint8Array;
}

interface ProcessedFileData {
  file: FileObject;
  urlObject: string;
}

const DocClientInstance = new TransactionDocumentClient(ENDPOINT);

/**
 * @deprecated use TransactionFilesGallery instead
 */
export const AltGallery: React.FC<Props> = ({
  fileList,
  title,
  text,
  subtitle,
  iconButton,
  disabled,
  canDelete,
  transactionID,
}) => {
  const [activeImage, setActiveImage] = useState(0);
  const [currentURL, setCurrentURL] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileListData, setFileListData] = useState<GalleryData[]>(fileList);
  const [documentList, setDocumentList] = useState<DocData[]>([]);
  const [deleting, setDeleting] = useState(false);

  const toggleOpen = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  };

  const fetchDocData = useCallback(
    async (d: DocData) => {
      try {
        const dataResult = await DocClientInstance.download(transactionID, d.reference);
        const data = dataResult.data;

        setDocumentList((prevState) =>
          prevState.map((doc) => {
            if (doc.reference === d.reference) {
              doc.data = data;
            }
            return doc;
          }),
        );

        return data;
      } catch {
        alert('Error fetching document data');
        return undefined;
      }
    },
    [transactionID],
  );

  const deleteFile = async () => {
    setIsLoading(true);
    setDeleting(false);

    const data = fileListData[activeImage];
    try {
      await DocClientInstance.deleteByName(data.key, data.bucket);
      setFileListData((prevState) => prevState.filter((f) => f.key !== data.key));
      setDocumentList((prevState) => {
        const updatedList = prevState.filter((d) => `${transactionID}-${d.reference}` !== data.key);
        if (updatedList.length === 0) {
          setIsOpen(false);
          return updatedList;
        }
        changeImage(0, updatedList);
        return updatedList;
      });

      setIsLoading(false);
    } catch {
      alert('File could not be deleted');
      setIsLoading(false);
    }
  };

  const downloadFile = () => {
    const image = documentList[activeImage];
    const el = document.createElement('a');
    el.download = image.reference;
    const blob = new Blob([image.data!], {
      type: getMimeType(image.reference) || '.png',
    });
    el.href = URL.createObjectURL(blob);
    el.click();
    el.remove();
  };

  const fetchImage = useCallback(
    async (doc: DocData) => {
      setIsLoading(true);
      try {
        const data = await fetchDocData(doc);
        if (data) {
          const blob = new Blob([data], {
            type: getMimeType(doc.reference) || '.png',
          });
          const currentURL = URL.createObjectURL(blob);
          setCurrentURL(currentURL);
        } else throw new Error('no doc found');
      } catch {
        alert('No documents were found');
        toggleOpen();
      } finally {
        setIsLoading(false);
      }
    },
    [fetchDocData],
  );

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const docs = await DocClientInstance.byTransactionID(transactionID);

      const reverseDoc = docs?.reverse();
      const galleryData = reverseDoc?.map((d) => ({
        key: `${transactionID}-${d.reference}`,
        description: d.description,
        bucket: 'kalos-transactions',
        typeId: d.typeId,
      }));
      const documentList = reverseDoc?.map((d) => ({
        reference: d.reference,
        id: transactionID,
      }));
      setFileListData(galleryData || []);
      setDocumentList(documentList || []);
      if (documentList && documentList.length > 0) {
        fetchImage(documentList[0]);
      }
    } catch {
      alert('No documents were found');
      toggleOpen();
    }
    setIsLoading(false);
  }, [transactionID, fetchImage]);

  const changeImage = (newImageIndex: number, newDocumentList?: DocData[]) => {
    setActiveImage(newImageIndex);

    const newCurrentDocument = newDocumentList?.at(newImageIndex) || documentList.at(newImageIndex);
    if (newCurrentDocument) {
      fetchImage(newCurrentDocument);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
    if (!isOpen) {
      //We shall reset just to have an even flow
      setFileListData([]);
      setDocumentList([]);
      setActiveImage(0);
    }
  }, [isOpen, fetchData]);

  const button = iconButton ? (
    <Tooltip content={text}>
      <span>
        <IconButton size="small" onClick={toggleOpen} disabled={disabled}>
          <ImageSearchTwoTone />
        </IconButton>
      </span>
    </Tooltip>
  ) : (
    <ButtonLib onClick={toggleOpen} disabled={disabled} label={text} />
  );

  const mimeType = getMimeType(fileListData[activeImage]?.key || '');

  const sectionTitle = useMemo(() => {
    if (subtitle) return subtitle;
    const label = WaiverTypes.find((a) => a.value === fileListData[activeImage]?.typeId)?.label;
    const description = fileListData[activeImage]?.description;
    const transactionDocumentType = description ? `Transaction Document Type: ${description}` : '';
    const waiverType = label ? `Waiver Status: ${label}` : '';

    return `${transactionDocumentType}  ${waiverType}`;
  }, [subtitle, fileListData, activeImage]);

  return (
    <>
      {button}

      <Modal
        open={isOpen}
        onClose={toggleOpen}
        fullScreen
        className="AltGalleryModal Modal-Remove-Scroll relative flex h-svh flex-col justify-between"
      >
        <SectionBar
          title={title}
          subtitle={sectionTitle}
          subtitleMinimized
          actions={[
            ...(canDelete
              ? [
                  {
                    label: '',
                    onClick: () => setDeleting(true),
                    startIcon: <DeleteIcon />,
                    disabled: !fileListData.length,
                  },
                ]
              : []),

            {
              label: '',
              onClick: downloadFile,
              disabled: !fileListData.length,
              startIcon: <CloudDownloadIcon />,
            },

            { label: 'Close', onClick: toggleOpen },
          ]}
          fixedActions
          className="AltGallerySectionBar"
        />

        <div className="flex flex-auto items-center justify-center">
          {isLoading ? (
            <Loader className="AltGalleryModal-Loader" />
          ) : fileListData.length ? (
            mimeType === 'application/pdf' ? (
              <iframe title="preview" src={currentURL} className="AltGalleryIframe" />
            ) : (
              <RotateZoomImage url={currentURL} />
            )
          ) : (
            <div className="absolute flex h-full w-full items-center justify-center">
              <span className="text-xl">No files found.</span>
            </div>
          )}
        </div>

        <SectionBar
          pagination={{
            count: fileListData.length,
            page: activeImage,
            onPageChange: changeImage,
          }}
          sticky={false}
          className="fixed bottom-0 left-0 right-0"
        />
      </Modal>

      {deleting && (
        <ConfirmDelete
          open
          onConfirm={deleteFile}
          onClose={() => setDeleting(false)}
          kind="this file"
          name=""
        />
      )}
    </>
  );
};

// TODO new AltGallery component using new UI and to be only content of the modal.
export const TransactionFilesGallery = ({
  title,
  subTitle,
  canDelete,
  transactionId,
  subtitle,
  waiverDisplay,
  onDelete,
}: {
  title: string;
  subTitle?: React.ReactNode;
  canDelete?: boolean;
  transactionId: number;
  subtitle?: string;
  waiverDisplay?: boolean;
  onDelete?: () => void;
}) => {
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [isConverting, setIsConverting] = useState(false);
  const [processedData, setProcessedData] = useState<ProcessedFileData | undefined>();
  const transformComponentRef = useRef<any>(null);
  const [rotation, setRotation] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

  // Observe container size changes
  useEffect(() => {
    const currentRef = containerRef.current;
    if (!currentRef) return;

    const updateDimensions = () => {
      if (currentRef) {
        const { width, height } = currentRef.getBoundingClientRect();
        setContainerDimensions({ width, height });
      }
    };

    // Initial measurement
    updateDimensions();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(currentRef);

    return () => {
      resizeObserver.unobserve(currentRef);
      resizeObserver.disconnect();
    };
  }, []);

  const filesMetaQuery = useTransactionDocumentsBatchQuery({
    filter: {
      transactionId,
    },
    select(data) {
      return data.results.toReversed() ?? [];
    },
  });

  const fileIds = useMemo(
    () => filesMetaQuery.data?.map((doc) => doc.fileId) ?? [],
    [filesMetaQuery.data],
  );

  const isFilesQueryEnabled = fileIds.length > 0;

  const filesQuery = useFilesBatchQuery({
    fileIds,
    enabled: isFilesQueryEnabled,
  });

  const currentFileData = useMemo(() => {
    if (!filesMetaQuery.data || !filesQuery.data) return null;

    const transactionDoc = filesMetaQuery.data[activeImageIdx];
    const fileData = filesQuery.data.find((f) => f?.id === transactionDoc?.fileId);

    return {
      transactionDoc,
      fileData,
    };
  }, [filesMetaQuery.data, filesQuery.data, activeImageIdx]);

  // Check if file record is missing
  const isFileRecordMissing = useMemo(() => {
    return (
      !filesQuery.isPending &&
      filesQuery.data &&
      currentFileData?.transactionDoc &&
      !currentFileData?.fileData
    );
  }, [filesQuery.isPending, filesQuery.data, currentFileData]);

  const currentFileQueryEnabled = !!currentFileData?.fileData?.name;
  const currentFileQuery = useTransactionDocumentDataByIdQuery({
    fileName: currentFileData?.fileData?.name || '',
    enabled: !!currentFileData?.fileData?.name,
  });

  // Handle processed data updates
  useEffect(() => {
    if (!currentFileQuery.data || !currentFileData?.fileData?.name) {
      return;
    }

    const fileName = currentFileData.fileData.name;
    setIsConverting(true);

    convertHeicToJpeg(currentFileQuery.data.data, fileName, getMimeType(fileName))
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        setProcessedData({
          file: currentFileQuery.data,
          urlObject: url,
        });
      })
      .catch((error) => {
        console.error('Error processing file:', error);
      })
      .finally(() => {
        setIsConverting(false);
      });

    return () => {
      setProcessedData((prev) => {
        if (prev?.urlObject) {
          URL.revokeObjectURL(prev.urlObject);
        }
        return undefined;
      });
    };
  }, [currentFileQuery.data, currentFileData?.fileData?.name]);

  // Add empty state handling
  const isEmpty = useMemo(() => {
    return !filesMetaQuery.isLoading && (!filesMetaQuery.data || filesMetaQuery.data.length === 0);
  }, [filesMetaQuery.isLoading, filesMetaQuery.data]);

  // Reset active image index when files are deleted
  useEffect(() => {
    if (isEmpty) {
      setActiveImageIdx(0);
      setProcessedData(undefined);
    }
  }, [isEmpty]);

  const downloadFile = async () => {
    if (!processedData?.file || !currentFileData?.fileData) {
      console.error('No file data found', { processedData, currentFileData });
      return;
    }

    const el = document.createElement('a');
    el.download = currentFileData.fileData.name;
    const blob = new Blob([processedData.file.data], {
      type: getMimeType(currentFileData.fileData.name) || '.png',
    });
    el.href = URL.createObjectURL(blob);
    el.click();
    el.remove();
  };

  const { mimeType, description } = useMemo(
    () => ({
      mimeType: getMimeType(currentFileData?.fileData?.name ?? ''),
      description: currentFileData?.transactionDoc?.description ?? '',
    }),
    [currentFileData],
  );

  const deleteFileMutation = useDeleteTransactionDocumentByIDMutation();
  const queryClient = useQueryClient();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const deleteFile = async () => {
    const data = currentFileData?.transactionDoc;
    if (!data) {
      alert('No file found');
      return;
    }

    try {
      await deleteFileMutation.mutateAsync(
        { fileId: data.fileId, transactionFileId: data.id },
        {
          onSuccess: () => {
            queryClient.setQueriesData<TransactionDocumentList>(
              {
                queryKey: [queryKeys.transactionDocument.root, queryKeys.transactionDocument.list],
              },
              (cache) => {
                if (cache && cache.results.at(0)?.transactionId === transactionId) {
                  const modifiedCache = cache.results.filter(
                    (cachedFile) => cachedFile.fileId !== data.fileId,
                  );
                  return TransactionDocumentList.create({
                    results: modifiedCache,
                    totalCount: modifiedCache.length,
                  });
                }
              },
            );
          },
        },
      );
      if (onDelete) onDelete();
      setActiveImageIdx(0);
    } catch {
      alert('File could not be deleted');
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };
  const sectionTitle = useMemo(() => {
    if (subtitle) return subtitle;

    const label = WaiverTypes.find(
      (a) => a.value === filesMetaQuery.data?.at(activeImageIdx)?.typeId,
    )?.label;
    const transactionDocumentType = description ? `Transaction Document Type: ${description}` : '';
    const waiverType = label ? `Waiver Status: ${label}` : '';
    if (waiverDisplay) {
      return `${transactionDocumentType}  ${waiverType}`;
    } else {
      return `${transactionDocumentType}`;
    }
  }, [subtitle, waiverDisplay, filesMetaQuery.data, description, activeImageIdx]);

  return (
    <Card className="relative flex h-full w-full flex-1 flex-col overflow-hidden">
      <CardHeader>
        <div>
          <CardTitle>{title}</CardTitle>
          {sectionTitle && <CardDescription>{sectionTitle}</CardDescription>}
          {subtitle && <CardDescription>Subtitle: {subtitle}</CardDescription>}
        </div>
        {subTitle}
        <div className="flex gap-2">
          {canDelete && (
            <Dialog onOpenChange={setIsDeleteDialogOpen} open={isDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="flex gap-1 text-xs sm:text-sm"
                  disabled={!currentFileQueryEnabled}
                >
                  <TrashIcon />
                  <span>Delete File</span>
                </Button>
              </DialogTrigger>

              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Confirm deletion of this file</DialogTitle>
                  <DialogDescription>Are you sure you want to delete this file</DialogDescription>
                </DialogHeader>
                <DialogFooter className="flex flex-row items-center justify-center gap-2">
                  <DialogClose asChild>
                    <Button disabled={deleteFileMutation.isPending} className="flex-1">
                      Cancel
                    </Button>
                  </DialogClose>
                  <Button
                    isLoading={deleteFileMutation.isPending}
                    disabled={deleteFileMutation.isPending}
                    className="flex-1"
                    onClick={deleteFile}
                    variant="destructive"
                  >
                    Confirm
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          <Button
            className="flex gap-1 text-xs sm:text-sm"
            disabled={!currentFileQueryEnabled}
            onClick={downloadFile}
          >
            <DownloadIcon />
            <span>Download File</span>
          </Button>
        </div>
      </CardHeader>

      <div
        ref={containerRef}
        className="AltGalleryModal flex h-[calc(100%_-138px)] w-full items-center justify-center"
      >
        {isEmpty ? (
          <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
            <ImageSearchTwoTone className="mb-4 size-16 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900">No Images Found</h3>
            <p className="text-sm text-gray-500">
              There are no images associated with this transaction.
            </p>
          </div>
        ) : isFileRecordMissing ? (
          <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
            <ImageSearchTwoTone className="mb-4 size-16 text-red-400" />
            <h3 className="text-lg font-medium text-gray-900">File Record Not Found</h3>
            <p className="text-sm text-gray-500">
              The transaction document exists, but the associated file record could not be loaded.
            </p>
            <p className="mt-2 text-xs text-gray-400">
              File ID: {currentFileData?.transactionDoc?.fileId}
            </p>
          </div>
        ) : (
          <div className="flex flex-1 flex-col overflow-hidden">
            {isConverting || filesMetaQuery.isLoading ? (
              <div className="flex h-full items-center justify-center">
                <Loader />
              </div>
            ) : (
              <div className="relative flex h-full flex-col">
                <div className="flex-1 overflow-hidden">
                  {processedData?.urlObject &&
                    (mimeType === 'application/pdf' ? (
                      <iframe
                        title="preview"
                        src={processedData.urlObject}
                        className="h-full w-full"
                        style={{
                          minHeight: '75vh',
                          width: '100%',
                          height: '100%',
                          border: 'none',
                          overflow: 'auto',
                        }}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center overflow-hidden">
                        <TransformWrapper
                          ref={transformComponentRef}
                          initialScale={1}
                          minScale={0.1}
                          maxScale={8}
                          limitToBounds={true}
                          centerOnInit={true}
                        >
                          <TransformComponent
                            wrapperStyle={{
                              width: '100%',
                              height: '100%',
                              overflow: 'hidden',
                            }}
                            contentStyle={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <img
                              src={processedData.urlObject}
                              alt={currentFileData?.fileData?.name || 'Transaction image'}
                              className="object-contain"
                              id="zoomableImage"
                              style={{
                                transform: `rotate(${rotation}deg)`,
                                maxWidth: containerDimensions.width
                                  ? `${containerDimensions.width}px`
                                  : '100%',
                                maxHeight: containerDimensions.height
                                  ? `${containerDimensions.height}px`
                                  : '100%',
                                width: 'auto',
                                height: 'auto',
                              }}
                            />
                          </TransformComponent>
                        </TransformWrapper>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {processedData?.urlObject && mimeType !== 'application/pdf' && (
        <div className="absolute bottom-12 left-0 right-0 w-full border-t-2 border-gray-300 bg-gray-100 p-2">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button
              className="text-xs sm:text-sm"
              onClick={() => {
                if (transformComponentRef.current) {
                  transformComponentRef.current.zoomIn();
                }
              }}
            >
              Zoom In
            </Button>
            <Button
              className="text-xs sm:text-sm"
              onClick={() => {
                if (transformComponentRef.current) {
                  transformComponentRef.current.zoomOut();
                }
              }}
            >
              Zoom Out
            </Button>
            <Button
              className="text-xs sm:text-sm"
              onClick={() => {
                if (transformComponentRef.current) {
                  transformComponentRef.current.resetTransform();
                  setRotation(0);
                }
              }}
            >
              Reset
            </Button>
            <Button
              className="text-xs sm:text-sm"
              onClick={() => {
                setRotation((prev) => (prev + 90) % 360);
              }}
            >
              <RotateCounterClockwiseIcon className="-scale-x-100" /> Rotate Right
            </Button>
            <Button
              className="text-xs sm:text-sm"
              onClick={() => {
                setRotation((prev) => (prev - 90 + 360) % 360);
              }}
            >
              <RotateCounterClockwiseIcon /> Rotate Left
            </Button>
          </div>
        </div>
      )}

      <CardFooter className="flex justify-center p-2">
        <DataTablePagination
          currentPage={activeImageIdx}
          pageCount={filesMetaQuery.data?.length ?? 0}
          setPage={setActiveImageIdx}
          disabled={
            filesMetaQuery.isPending ||
            filesQuery.isPending ||
            (currentFileQuery.isPending && !isFileRecordMissing)
          }
        />
      </CardFooter>
    </Card>
  );
};

export const RotateZoomImage = ({
  url,
  data,
  fullScreenEnabled = false,
  disableTransform = false,
  downloadFilename,
}: {
  url: string;
  data?: string;
  fullScreenEnabled?: boolean;
  disableTransform?: boolean;
  downloadFilename?: string;
}) => {
  const [rotation, setRotation] = useState<Deg>(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [fullViewRotation, setFullViewRotation] = useState<Deg>(0);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isFullView, setIsFullView] = useState(false);
  const [isDialogFullView, setIsDialogFullView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset full view rotation when entering/exiting full view
  useEffect(() => {
    if (isFullView) {
      setFullViewRotation(rotation);
    }
  }, [isFullView, rotation]);

  useEffect(() => {
    const currentRef = containerRef.current;
    if (!currentRef) return;

    const updateDimensions = () => {
      if (currentRef) {
        const { width, height } = currentRef.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    // Initial measurement
    updateDimensions();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(currentRef);

    return () => {
      resizeObserver.unobserve(currentRef);
      resizeObserver.disconnect();
    };
  }, []);

  const imageSrc = data ? data : url;

  const handleDownload = async () => {
    if (!downloadFilename) return;
    setIsDownloading(true);
    try {
      if (imageSrc.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = imageSrc;
        link.download = downloadFilename;
        link.click();
      } else {
        const response = await fetch(imageSrc);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = downloadFilename;
        link.click();
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isFullView) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-white">
        <div className="relative flex h-full w-full flex-col">
          <TransformWrapper
            initialScale={1}
            minScale={0.1}
            maxScale={8}
            limitToBounds={false}
            centerOnInit={true}
            disabled={disableTransform}
            wheel={{
              disabled: disableTransform,
            }}
            pinch={{
              disabled: disableTransform,
            }}
          >
            {({ zoomIn, zoomOut, resetTransform }) => (
              <>
                <div className="absolute inset-0 flex-1">
                  <TransformComponent
                    wrapperStyle={{
                      width: '100%',
                      height: '100%',
                    }}
                    contentStyle={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <img
                      src={imageSrc}
                      style={{
                        transform: `rotate(${fullViewRotation}deg)`,
                        transformOrigin: 'center',
                        width: 'auto',
                        height: 'auto',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                      }}
                      alt="Document preview"
                    />
                  </TransformComponent>
                </div>
                <div className="absolute bottom-0 left-0 right-0 flex flex-wrap items-center justify-center gap-1 bg-white bg-opacity-95 px-2 py-1 shadow-lg">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() =>
                      setFullViewRotation((prev) => {
                        if (prev === 270) return 0;
                        return (prev + 90) as Deg;
                      })
                    }
                  >
                    <RotateCounterClockwiseIcon className="size-3 -scale-x-100" />
                    <span className="ml-1 hidden sm:inline">Right</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() =>
                      setFullViewRotation((prev) => {
                        if (prev === 0) return 270;
                        return (prev - 90) as Deg;
                      })
                    }
                  >
                    <RotateCounterClockwiseIcon className="size-3" />
                    <span className="ml-1 hidden sm:inline">Left</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => zoomIn()}
                  >
                    <span>+</span>
                    <span className="ml-1 hidden sm:inline">Zoom</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => zoomOut()}
                  >
                    <span>−</span>
                    <span className="ml-1 hidden sm:inline">Zoom</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => {
                      resetTransform();
                      setFullViewRotation(0);
                    }}
                  >
                    <span>↺</span>
                    <span className="ml-1 hidden sm:inline">Reset</span>
                  </Button>
                  {downloadFilename && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={handleDownload}
                      disabled={isDownloading}
                    >
                      <DownloadIcon className="size-3" />
                      <span className="ml-1 hidden sm:inline">Download</span>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => setIsFullView(false)}
                  >
                    <ExitFullScreenIcon className="size-3" />
                    <span className="ml-1 hidden sm:inline">Exit</span>
                  </Button>
                </div>
              </>
            )}
          </TransformWrapper>
        </div>
      </div>
    );
  }

  return (
    <>
      <TransformWrapper
        initialScale={1}
        minScale={0.1}
        maxScale={8}
        limitToBounds={true}
        centerOnInit={true}
        disabled={disableTransform}
        wheel={{
          disabled: disableTransform,
        }}
        pinch={{
          disabled: disableTransform,
        }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <div ref={containerRef} className="flex h-full w-full flex-col">
            <div className="relative flex h-[calc(100%-60px)] w-full items-center justify-center overflow-hidden">
              <TransformComponent
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                }}
                contentStyle={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={imageSrc}
                  style={{
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: 'center',
                    maxWidth: dimensions.width ? `${dimensions.width}px` : '100%',
                    maxHeight: dimensions.height
                      ? `${dimensions.height - 84}px`
                      : 'calc(100% - 84px)',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                  alt="Document preview"
                />
              </TransformComponent>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 p-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setRotation((prev) => {
                    if (prev === 270) return 0;
                    return (prev + 90) as Deg;
                  })
                }
              >
                <RotateCounterClockwiseIcon className="h-4 w-4 -scale-x-100" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() =>
                  setRotation((prev) => {
                    if (prev === 0) return 270;
                    return (prev - 90) as Deg;
                  })
                }
              >
                <RotateCounterClockwiseIcon className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => zoomIn()}>
                <span className="text-lg leading-none">+</span>
              </Button>
              <Button variant="ghost" size="icon" onClick={() => zoomOut()}>
                <span className="text-lg leading-none">−</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  resetTransform();
                  setRotation(0);
                }}
              >
                <span className="text-base leading-none">↺</span>
              </Button>
              {downloadFilename && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDownload}
                  disabled={isDownloading}
                  aria-label="Download"
                >
                  <DownloadIcon className="h-4 w-4" />
                </Button>
              )}
              {fullScreenEnabled && (
                <Button variant="ghost" size="icon" onClick={() => setIsDialogFullView(true)}>
                  <EnterFullScreenIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}
      </TransformWrapper>

      {isDialogFullView && (
        <Dialog open={isDialogFullView} onOpenChange={setIsDialogFullView}>
          <DialogContent className="h-[98vh] w-[98vw] max-w-none p-0">
            <TransformWrapper
              initialScale={1}
              minScale={0.1}
              maxScale={8}
              limitToBounds={false}
              centerOnInit={true}
              disabled={disableTransform}
              wheel={{
                disabled: disableTransform,
              }}
              pinch={{
                disabled: disableTransform,
              }}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <div className="relative flex h-full w-full flex-col">
                  <div className="absolute inset-0 flex-1">
                    <TransformComponent
                      wrapperStyle={{
                        width: '100%',
                        height: '100%',
                      }}
                      contentStyle={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <img
                        src={imageSrc}
                        style={{
                          transform: `rotate(${rotation}deg)`,
                          transformOrigin: 'center',
                          width: 'auto',
                          height: 'auto',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                        }}
                        alt="Document preview"
                      />
                    </TransformComponent>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 flex flex-wrap items-center justify-center gap-1 bg-white bg-opacity-95 px-2 py-1 shadow-lg">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() =>
                        setRotation((prev) => {
                          if (prev === 270) return 0;
                          return (prev + 90) as Deg;
                        })
                      }
                    >
                      <RotateCounterClockwiseIcon className="size-3 -scale-x-100" />
                      <span className="ml-1 hidden sm:inline">Right</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() =>
                        setRotation((prev) => {
                          if (prev === 0) return 270;
                          return (prev - 90) as Deg;
                        })
                      }
                    >
                      <RotateCounterClockwiseIcon className="size-3" />
                      <span className="ml-1 hidden sm:inline">Left</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() => zoomIn()}
                    >
                      <span>+</span>
                      <span className="ml-1 hidden sm:inline">Zoom</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() => zoomOut()}
                    >
                      <span>−</span>
                      <span className="ml-1 hidden sm:inline">Zoom</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() => {
                        resetTransform();
                        setRotation(0);
                      }}
                    >
                      <span>↺</span>
                      <span className="ml-1 hidden sm:inline">Reset</span>
                    </Button>
                    {downloadFilename && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-xs"
                        onClick={handleDownload}
                        disabled={isDownloading}
                      >
                        <DownloadIcon className="size-3" />
                        <span className="ml-1 hidden sm:inline">Download</span>
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() => setIsDialogFullView(false)}
                    >
                      <ExitFullScreenIcon className="size-3" />
                      <span className="ml-1 hidden sm:inline">Close</span>
                    </Button>
                  </div>
                </div>
              )}
            </TransformWrapper>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
